import type { DocumentTabType, OpenTab } from '@/components/features/document-tabs'
import { parentFolderFromDocumentPath } from '@/lib/local-text-import'

export type ResolveCreateFolderPathInput = {
  activeTab: OpenTab | undefined
  selectedProjectId?: string | null
  workspaceContentRoot?: string | null
  /** First active workspace root when tab has no workspace (e.g. Home). */
  fallbackWorkspaceContentRoot?: string | null
  /**
   * Workspace Desk path (`{contentRoot}/users/{slug}`) when known.
   * Used when no document / explorer / browse folder is in focus.
   */
  deskFolderPath?: string | null
  /**
   * Content roots of the currently scoped workspace(s).
   * Browse focus (`selectedProjectId`) is ignored when it sits outside these roots.
   */
  scopedWorkspaceContentRoots?: Array<string | null | undefined> | null
}

const DOCUMENT_TAB_TYPES = new Set<string>([
  'work-spec',
  'note',
  'voice-memo',
  'flow',
  'todo',
  'meeting',
  'idea',
  'research',
  'skill',
  'quiz',
] satisfies DocumentTabType[])

/** True when the tab is a stored Maater document (not Home / explorer / integration). */
export function isDocumentContentTab(tab: OpenTab | undefined): boolean {
  return Boolean(tab && DOCUMENT_TAB_TYPES.has(tab.type))
}

/**
 * Browse folder highlight is global + persisted. Home / Inbox / Search / workspace
 * landing tabs are not that folder - New must not inherit a leftover path
 * (e.g. hop-studios/clients/athena-marketing while scoped to Personal).
 */
export function shouldInheritBrowseFocusForCreate(tab: OpenTab | undefined): boolean {
  if (!tab) return true
  if (tab.type === 'explorer') return true
  if (isDocumentContentTab(tab)) return true
  return false
}

function folderPathBelongsToWorkspaceRoots(
  folderPath: string,
  roots: Array<string | null | undefined> | null | undefined,
): boolean {
  if (!roots || roots.length === 0) return true
  const normalized = normalizeFolderPath(folderPath)
  if (!normalized) return false
  return roots.some((root) => {
    const prefix = normalizeFolderPath(root)
    if (!prefix) return false
    return normalized === prefix || normalized.startsWith(`${prefix}/`)
  })
}

function usableFolderPath(path: string | null | undefined): string | null {
  const normalized = normalizeFolderPath(path)
  if (!normalized || isOpaqueFolderFocusPath(normalized)) return null
  return normalized
}

/** Parent of the open document, or the explorer tab folder. Not persisted Browse highlight. */
function resolveTabFolderPath(activeTab: OpenTab | undefined): string | null {
  if (isDocumentContentTab(activeTab)) {
    const pathHint =
      (activeTab!.filePath && String(activeTab!.filePath).trim()) ||
      (typeof activeTab!.id === 'string' && activeTab!.id.includes('/')
        ? activeTab!.id
        : null)
    const parent = parentFolderFromDocumentPath(pathHint)
    return usableFolderPath(parent)
  }

  if (activeTab?.type === 'explorer' && activeTab.explorerData?.folderPath) {
    return usableFolderPath(activeTab.explorerData.folderPath)
  }

  return null
}

/** Normalize a folder path: trim slashes, collapse backslashes. */
function normalizeFolderPath(path: string | null | undefined): string {
  if (path == null) return ''
  return String(path)
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const STORAGE_HEX_RE = /^[a-f0-9]{32}$/i

/**
 * True when browse/focus value is an opaque storage/org id, not a real folder path.
 * Examples: bare 32-hex org id, UUID, `ws/{32hex}` root alone.
 * These must never appear as "New in {label}" copy.
 */
export function isOpaqueFolderFocusPath(path: string | null | undefined): boolean {
  const normalized = normalizeFolderPath(path)
  if (!normalized) return false
  if (UUID_RE.test(normalized)) return true
  if (STORAGE_HEX_RE.test(normalized)) return true
  if (/^ws\/[a-f0-9]{32}$/i.test(normalized)) return true
  return false
}

/** True when a single path segment is opaque (hex org id / ws token). */
function isOpaquePathSegment(segment: string): boolean {
  return (
    STORAGE_HEX_RE.test(segment) ||
    UUID_RE.test(segment) ||
    /^ws$/i.test(segment)
  )
}

/**
 * Resolve where a new document should be created.
 *
 * Priority: parent of active document > explorer folder > browse folder
 * (only while looking at explorer / a document) > desk > workspace content root.
 *
 * Opaque browse focus (bare org hex / UUID) is skipped so create lands on desk/root.
 * Browse paths outside `scopedWorkspaceContentRoots` are skipped the same way.
 */
export function resolveCreateFolderPath({
  activeTab,
  selectedProjectId,
  workspaceContentRoot,
  fallbackWorkspaceContentRoot,
  deskFolderPath,
  scopedWorkspaceContentRoots,
}: ResolveCreateFolderPathInput): string {
  const fromTab = resolveTabFolderPath(activeTab)
  if (fromTab) return fromTab

  const browsePath = usableFolderPath(selectedProjectId)
  if (
    browsePath &&
    shouldInheritBrowseFocusForCreate(activeTab) &&
    folderPathBelongsToWorkspaceRoots(browsePath, scopedWorkspaceContentRoots)
  ) {
    return browsePath
  }

  const deskPath = usableFolderPath(deskFolderPath)
  if (deskPath) return deskPath

  const rootPath = usableFolderPath(workspaceContentRoot)
  if (rootPath) return rootPath

  const fallbackRoot = usableFolderPath(fallbackWorkspaceContentRoot)
  if (fallbackRoot) return fallbackRoot

  return ''
}

/**
 * Folder the user is actually looking at: open document parent, explorer folder,
 * or persisted Browse highlight. Does not fall back to desk or workspace root.
 * Drive import uses this; + New does not (Home must not inherit a leftover folder).
 */
export function resolveFocusedBrowseFolderPath(
  input: Pick<ResolveCreateFolderPathInput, 'activeTab' | 'selectedProjectId'>,
): string | null {
  const fromTab = resolveTabFolderPath(input.activeTab)
  if (fromTab) return fromTab
  return usableFolderPath(input.selectedProjectId)
}

/**
 * Short label for "New in {folder}" menu copy.
 * Never returns bare org hex / UUID segments - walks up for a human segment or fallback.
 */
export function folderLabelForCreatePath(
  folderPath: string,
  fallback = 'workspace',
): string {
  const normalized = normalizeFolderPath(folderPath)
  if (!normalized) return fallback
  if (isOpaqueFolderFocusPath(normalized)) return fallback

  const parts = normalized.split('/').filter(Boolean)
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i]
    if (part && !isOpaquePathSegment(part)) return part
  }
  return fallback
}
